import React, { useState, useEffect } from "react";
import { Mic, MicOff, Crown, X, Check, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuthContext } from "@/contexts/AuthContext";
import { RealtimeChannel } from "@supabase/supabase-js";

interface QueueUser {
  id: string;
  name: string;
  photo_url?: string | null;
  requestedAt: string;
}

interface SpeakQueueProps {
  channel: RealtimeChannel | null;
  isHost: boolean;
  currentSpeakerId: string | null;
  onRequestSpeak: () => void;
  onCancelRequest: () => void;
  onGrantSpeak: (userId: string) => void;
  onRevokeSpeak: (userId: string) => void;
}

const SpeakQueue: React.FC<SpeakQueueProps> = ({
  channel,
  isHost,
  currentSpeakerId,
  onRequestSpeak,
  onCancelRequest,
  onGrantSpeak,
  onRevokeSpeak,
}) => {
  const { user } = useAuthContext();
  const [queue, setQueue] = useState<QueueUser[]>([]);
  const [isInQueue, setIsInQueue] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Listen for queue updates
  useEffect(() => {
    if (!channel) return;

    const handlePresence = () => {
      const state = channel.presenceState();
      const queueList: QueueUser[] = [];

      Object.values(state).forEach((presences: unknown[]) => {
        (presences as Array<{
          id: string;
          name: string;
          photo_url?: string | null;
          speak_requested_at?: string;
          is_speaking?: boolean;
        }>).forEach((p) => {
          if (p.speak_requested_at && p.id !== currentSpeakerId) {
            queueList.push({
              id: p.id,
              name: p.name,
              photo_url: p.photo_url,
              requestedAt: p.speak_requested_at,
            });
          }
          if (p.id === user?.id) {
            setIsInQueue(!!p.speak_requested_at);
            setIsSpeaking(p.is_speaking || false);
          }
        });
      });

      // Sort by request time
      queueList.sort((a, b) => new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime());
      setQueue(queueList);
    };

    channel.on("presence", { event: "sync" }, handlePresence);
    handlePresence();

    return () => {
      channel.unsubscribe();
    };
  }, [channel, currentSpeakerId, user]);

  const handleRequest = () => {
    if (!channel || !user) return;
    onRequestSpeak();
    channel.track({
      ...channel.presenceState()[user.id]?.[0],
      speak_requested_at: new Date().toISOString(),
    } as any);
  };

  const handleCancel = () => {
    if (!channel || !user) return;
    onCancelRequest();
    const currentPresence = channel.presenceState()[user.id]?.[0] as any;
    if (currentPresence) {
      const { speak_requested_at, ...rest } = currentPresence;
      channel.track(rest);
    }
  };

  return (
    <div className="space-y-3">
      {/* Current Speaker */}
      {currentSpeakerId && (
        <div className="p-3 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-primary" />
            <div>
              <div className="text-sm font-medium">Current Speaker</div>
              <div className="text-xs text-muted-foreground">Mic is active</div>
            </div>
          </div>
          {isHost && (
            <Button variant="ghost" size="sm" onClick={() => onRevokeSpeak(currentSpeakerId)}>
              <MicOff className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      {/* User's Request Status */}
      {!isHost && (
        <div>
          {isSpeaking ? (
            <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mic className="w-5 h-5 text-green-500" />
                <div>
                  <div className="text-sm font-medium">You're Speaking</div>
                  <div className="text-xs text-muted-foreground">Your mic is active</div>
                </div>
              </div>
            </div>
          ) : isInQueue ? (
            <div className="p-3 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-orange-500" />
                <div>
                  <div className="text-sm font-medium">In Queue</div>
                  <div className="text-xs text-muted-foreground">Waiting for host...</div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button onClick={handleRequest} className="w-full gap-2" variant="outline">
              <Mic className="w-4 h-4" />
              Request to Speak
            </Button>
          )}
        </div>
      )}

      {/* Queue List (Host only) */}
      {isHost && queue.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold">Queue ({queue.length})</div>
          {queue.map((queuedUser, index) => (
            <div
              key={queuedUser.id}
              className="p-3 rounded-xl border border-border bg-white flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                  {index + 1}
                </div>
                <div>
                  <div className="text-sm font-medium">{queuedUser.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(queuedUser.requestedAt).toLocaleTimeString()}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => onGrantSpeak(queuedUser.id)}>
                <Check className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {isHost && queue.length === 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground">No requests in queue</div>
      )}
    </div>
  );
};

export default SpeakQueue;

